import React, {
  useState, useEffect, useMemo, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
// import { AgSparklineOptions } from "ag-charts-community";
import { AgGridReact } from 'ag-grid-react';
import { ToggleWrapOuter, ToggleSlide } from './BudgetTableCss';
import GroupEditModal from './form/GroupEditModal';
// import Notify from '../../../../shared/components/widgets/Notify';
import config from '../../../config';
import Panel from '../../../shared/components/Panel';
// import Slider from '../../../../shared/components/range-slider/Slider';
import {
  currencyFormatter, formatDecimalToFixedTwoPercent, formatNumberOnePlaceDefaultToZero, formatNumberDefaultToZero, getExportPrefix, // formatDecimalToFixedTwoPercentDirect,
} from '../../../shared/components/table/functions';
import { getPlatform } from '../../../shared/components/domainSupport';

const apiUrl = config.isProdEnv ? config.serverProdUrl : config.serverDevUrl;

export default function BudgetTable({
  allById, internalById, allByDate, internalByDate, retailer, title, allGraphLoad, internalGraphLoad, gridTheme, reportDays, forceRefresh, setForceRefresh, budgetAccountData, activeDayOptions, retailerConfig,
}) {
  const gridRef = useRef();
  const sellerSelect = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const [combinedItems, setCombinedItems] = useState([]);
  const [budgetItems, setBudgetItems] = useState([]);
  const [panelLoad, setPanelLoad] = useState(false);
  const [hideNonEssentialBudget, setHideNonEssentialBudget] = useState(true);
  const [hideNonEssentialSpendControls, setHideNonEssentialSpendControls] = useState(false);
  const [hideNonEssentialMetrics, setHideNonEssentialMetrics] = useState(true);
  const [hideNonEssentialAdvancedMetrics, setHideNonEssentialAdvancedMetrics] = useState(false);
  const [hideNonEssentialSparklines, setHideNonEssentialSparklines] = useState(true);
  const [adminView, setAdminView] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRowData, setEditRowData] = useState({});
  const [disableButton, setDisableButton] = useState(false);

  const platfromSpec = getPlatform();
  const unassignedText = '*UNASSIGNED*';

  const getData = () => {
    const retailerPath = `${retailer.toLowerCase()}/`;
    const budgetUrl = `${apiUrl}/accounts/${retailerPath}budget-group-daily?accountId=${sellerSelect.value}&platform=${platfromSpec.nameKey}`;

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };

    setPanelLoad(true);
    setBudgetItems([]);

    fetch(budgetUrl, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      }).then((data) => {
        // Transform CSV strings into structured budgetGroupRecords array
        const transformedData = data.budgetGroupData.map(item => {
          const ids = item.budget_group_daily_id_csv ? item.budget_group_daily_id_csv.split(',').map(id => parseInt(id.trim(), 10)) : [];

          // Parse ad_type_csv - API returns individual ad types like "SP,SD,SB"
          // We need to group them: SP+SD together = "SP,SD", SB alone = "SB", ST alone = "ST"
          const rawAdTypes = item.ad_type_csv ? item.ad_type_csv.split(',').map(at => at.trim()) : [];
          const adTypeGroups = [];

          // Check if we have both SP and SD - they should be grouped together as "SP,SD"
          const hasSP = rawAdTypes.includes('SP');
          const hasSD = rawAdTypes.includes('SD');

          if (hasSP && hasSD) {
            // SP and SD are together as one group
            adTypeGroups.push('SP,SD');
          } else if (hasSP) {
            adTypeGroups.push('SP');
          } else if (hasSD) {
            adTypeGroups.push('SD');
          }

          // Check for SB and ST separately
          if (rawAdTypes.includes('SB')) {
            adTypeGroups.push('SB');
          }
          if (rawAdTypes.includes('ST')) {
            adTypeGroups.push('ST');
          }

          // Parse allocation percentages from budget_json if available
          let allocations = [100, 0, 0]; // Default: 100% SP+SD
          try {
            const budgetJson = JSON.parse(item.budget_json || '{}');
            if (budgetJson.allocation?.percentages) {
              allocations = [
                budgetJson.allocation.percentages.sp_sd_percent || 0,
                budgetJson.allocation.percentages.sb_percent || 0,
                budgetJson.allocation.percentages.st_percent || 0,
              ];
            }
          } catch (e) {
            // Keep defaults
          }

          // Build structured records array - map each ad type to its ID and allocation
          const budgetGroupRecords = [];
          const standardOrder = ['SP,SD', 'SB', 'ST'];

          standardOrder.forEach((stdAdType, stdIndex) => {
            // Find matching ad type group in the response (may be out of order)
            const adTypeIndex = adTypeGroups.findIndex(at => at === stdAdType);

            // If we found this ad type in the response, use its corresponding ID
            // Otherwise, use 0 (not present in the BG)
            const id = adTypeIndex !== -1 ? (ids[adTypeIndex] || 0) : 0;

            budgetGroupRecords.push({
              budget_group_daily_id: id,
              ad_type: stdAdType,
              allocation_percent: allocations[stdIndex],
            });
          });

          return {
            ...item,
            budgetGroupRecords, // Add structured array
            // Keep originals for backwards compatibility during transition
          };
        });

        setPanelLoad(false);
        setBudgetItems(transformedData);
      }).catch(() => {
        setPanelLoad(false);
      });
  };

  useEffect(() => {
    getData();
  }, [sellerSelect.value, forceRefresh, retailer]);

  // useEffect(() => {
  //   const newMoPacos = ((pacosNonBrandAvg / 100) + (pacosBrandAvg / 100)) / 2;
  //   setMonthlyPacos(newMoPacos || 0);
  // }, [pacosBrandAvg, pacosNonBrandAvg]);

  useEffect(() => {
    if (!gridRef.current?.api) return;

    // If you’re using value cache elsewhere, also do:
    // gridRef.current.api.expireValueCache?.();

    gridRef.current.api.refreshCells({
      columns: ['mtd_ppc_spend', 'mtd_ppc_sales', 'mtd_ppc_acos', 'mtd_ppc_roas'],
      force: true,
    });

    // Recompute group/footer aggregations so the footer uses fresh sums
    gridRef.current.api.refreshClientSideRowModel('aggregate');
  }, [combinedItems]); // or any state that changes the underlying row values/includes


  useEffect(() => {
    if (allById && internalById && allByDate && internalByDate) {
      const newBudgetItems = [];
      const getName = (currentName) => {
        if (currentName === '') {
          return unassignedText;
        }
        return currentName;
      }
      budgetItems?.forEach(budgetItemRow => {
        if (budgetItemRow.budget_group_name || budgetItemRow.budget_group_name === '') {
          const allSpendChange = [];
          const allSalesChange = [];
          const allAcosChange = [];
          const internalSpendChange = [];
          const internalSalesChange = [];
          const internalAcosChange = [];
          // Build Change Sparkline
          allByDate?.forEach(allDate => {
            allSpendChange.push(allDate[`spend_${budgetItemRow.budget_group_name}`]);
            allSalesChange.push(allDate[`sales_${budgetItemRow.budget_group_name}`]);
            allAcosChange.push((allDate[`spend_${budgetItemRow.budget_group_name}`] && allDate[`sales_${budgetItemRow.budget_group_name}`] && allDate[`sales_${budgetItemRow.budget_group_name}`] > 0) ? (allDate[`spend_${budgetItemRow.budget_group_name}`] / allDate[`sales_${budgetItemRow.budget_group_name}`]) : null);
          });
          internalByDate?.forEach(internalDate => {
            internalSpendChange.push(internalDate[`spend_${budgetItemRow.budget_group_name}`]);
            internalSalesChange.push(internalDate[`sales_${budgetItemRow.budget_group_name}`]);
            internalAcosChange.push((internalDate[`spend_${budgetItemRow.budget_group_name}`] && internalDate[`sales_${budgetItemRow.budget_group_name}`] && internalDate[`sales_${budgetItemRow.budget_group_name}`] > 0) ? (internalDate[`spend_${budgetItemRow.budget_group_name}`] / internalDate[`sales_${budgetItemRow.budget_group_name}`]) : null);
          });
          const allMetricRow = allById?.group?.[budgetItemRow.budget_group_name] || {};
          const internalMetricRow = internalById?.group?.[budgetItemRow.budget_group_name] || {};
          newBudgetItems.push({ ...budgetItemRow, name: getName(budgetItemRow.budget_group_name), ...allMetricRow, ...internalMetricRow, allSpendChange, allSalesChange, allAcosChange, internalSpendChange, internalSalesChange, internalAcosChange });
        }
      });
      setCombinedItems(newBudgetItems);
    }
  }, [allById, internalById, allByDate, internalByDate, budgetItems]);

  const onGridReady = useCallback(() => {
    gridRef.current.api.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }, []);

  const readableActiveDays = (activeDaysCsv) => {
    const readableArray = [];
    const activeDaysArray = activeDaysCsv?.split(',') || [];
    activeDaysArray.forEach((day, pos) => {
      if (day === '1') {
        readableArray.push(moment().isoWeekday(pos + 1).format('dddd'));
      }
    });
    return readableArray.join(', ');
  }

  // const updateBudgetGroupDropdown = (selectedOption, e, itemName) => {
  //   const budgetItemsUpdates = [...budgetItems]; // clone array
  //   const { name: groupName } = e;

  //   const objIndex = budgetItemsUpdates.findIndex((obj => obj.name === groupName));

  //   budgetItemsUpdates[objIndex][itemName] = selectedOption.value;
  //   setBudgetItems(budgetItemsUpdates);
  // }

  // const updateBudgetGroup = (e) => {
  //   const budgetItemsUpdates = [...budgetItems]; // clone array
  //   const { name, value, dataset } = e.target;
  //   const { groupName } = dataset;
  //   const objIndex = budgetItemsUpdates.findIndex((obj => obj.name === groupName));
  //   const itemName = name;
  //   const regex = name === 'portfolioIdsCsv' ? /[^\-0-9.,]/g : /[^\-0-9.]/g;
  //   budgetItemsUpdates[objIndex][itemName] = ['pacos', 'tacos', 'pacosBrand', 'pacosNonBrand'].includes(name) ? (value.replace(regex, '') / 100) : value.replace(regex, '');
  //   setBudgetItems(budgetItemsUpdates);
  // }

  const budgetStateOptions = [
    { label: 'Enabled', value: 'enabled' },
    { label: 'Disabled', value: 'disabled' },
    { label: 'Draft', value: 'draft' },
    { label: 'Completed', value: 'completed' },
  ];

  const limitlessBudgetOptions = [
    { label: 'Strict Budget', value: 0 },
    { label: 'No Budget', value: 1 },
    { label: 'Flexible Budget', value: 2 },
  ];

  const includesOptions = [
    { label: 'All ADs', value: '' },
    { label: 'Limitless ADs', value: 'limitless' },
  ];

  const spendPriorityOptions = [
    { label: 'Most Brand', value: '-2' },
    { label: 'More Brand', value: '-1' },
    { label: 'Brand and NonBrand', value: '0' },
    { label: 'More NonBrand', value: '1' },
    { label: 'Most NonBrand', value: '2' },
  ];

  const spendTacticOptions = [
    { label: 'Max Performance', value: 'max_perf' },
    { label: 'Full Budget Spend', value: 'full_spend' },
  ];

  const spendSpeedOptions = [
    { label: 'Standard', value: 'speed_level_1' },
    { label: 'Fast', value: 'speed_level_2' },
    { label: 'Fastest', value: 'speed_level_3' },
  ];

  const itemTypeOptions = [
    { label: 'SKU', value: 'SKU' },
    { label: 'ASIN', value: 'ASIN' },
    { label: 'ITEM', value: 'ITEM' },
  ];

  const itemSelectionOptions = [
    { label: 'All', value: 'ALL' },
    { label: 'Top', value: 'TOP' },
  ];

  const destinationOptions = [
    { label: 'On', value: 'ON' },
    { label: 'Off', value: 'OFF' },
  ];

  const setupFlowOptions = [
    { label: 'Quick Setup', value: 'quick' },
    { label: 'Advanced Setup', value: 'advanced' },
  ];

  const agencyInternalOptions = [
    { label: 'Agency View', value: false },
    { label: 'Limitless View', value: true },
  ];

  const budgetDriverOptions = [
    { label: 'Daily', value: 'daily' },
    { label: 'Monthly', value: 'monthly' },
  ];

  const costTargetTypeOptions = [
    { label: 'Blended', value: 'blended' },
    { label: 'Brand/NonBrand', value: 'bnb' },
  ];

  // const findOption = (value, options) => {
  //   const foundObject = options.find(item => item.value === value);
  //   if (foundObject) {
  //     return foundObject;
  //   }
  //   return options[0];
  // };

  // function extractValues(mappings) {
  //   return mappings.map(map => map.value);
  // }

  function lookupValue(mappings, valueKey) {
    return mappings.find(map => map.value === valueKey);
  }

  const aggFuncs = useMemo(() => {
    const aggFuncsReturn = {
      customAvg: params => {
        let count = 0;
        let total = 0;
        params.values.forEach(value => {
          // if (value > 0) {
          count += 1;
          total += value;
          // }
        });
        const returnValue = count > 0 ? (total / count) : 0;
        return returnValue.toFixed(1);
      },
    };
    return aggFuncsReturn;
  }, []);

  const changeShowEditModal = (data) => {
    setEditRowData(data);
    setShowEditModal(!showEditModal);
  };

  const countActiveDaysInMonth = (activeDaysCsv, startDate, endDate) => {
    let count = 0;
    const today = moment();
    const daysInMonth = today.daysInMonth();
    for (let day = 1; day <= daysInMonth; day += 1) {
      const checkDay = moment([today.year(), today.month(), day]);
      const dayOfWeekNum = checkDay.isoWeekday() - 1;
      if (activeDaysCsv.split(',')[dayOfWeekNum] === '1' && checkDay >= moment(startDate) && (checkDay <= moment(endDate) || endDate === null)) {
        count += 1;
      }
    }
    return count;
  }

  const addNewDefault = {
    setupFlow: setupFlowOptions[0],
    agencyInternal: agencyInternalOptions[0],
    budgetDriver: budgetDriverOptions[0],
    costTargetType: costTargetTypeOptions[0],
  }

  // AG Grid variables
  /* consider these as well (for reference)
    sortable: true,
    comparator: (valueA, valueB) => compareNumber(valueA, valueB),
    resizable: true,
  */
  const columnDefs = [
    {
      field: 'editBg',
      headerName: 'BG',
      editable: false,
      cellRenderer: (params) => {
        if (params.node?.footer) {
          return '';
        }
        return <button type="submit" onClick={() => changeShowEditModal(params.data)}>Edit Row</button>;
      },
    },
    // {
    //   field: 'editItems',
    //   headerName: 'Items',
    //   editable: false,
    //   cellRenderer: (params) => {
    //     if (params.node?.footer) {
    //       return '';
    //     }
    //     return <button type="submit" onClick={() => changeShowEditModal(params.data)}>Edit Items</button>;
    //   },
    // },
    {
      field: 'budget_group_daily_id',
      headerName: 'Budget Group Daily ID (hide later)',
      editable: false,
      hide: true,
      suppressColumnsToolPanel: !adminView,
    },
    {
      field: 'calcState',
      headerName: 'State',
      editable: false,
      valueGetter: params => {
        let currentState = 'draft';
        if (params.node?.footer) {
          return '';
        }
        if (params.data?.state) {
          currentState = params.data.end_date && moment(params.data.end_date).format('YYYYMMDD') < moment().format('YYYYMMDD') && (params.data.state === 'enabled' || params.data.state === 'disabled') ? 'completed' : params.data.state;
        }
        const dispalyObj = lookupValue(budgetStateOptions, currentState);
        return dispalyObj?.label || '';
      },
    },
    {
      field: 'ad_type_csv',
      headerName: 'AD Type',
      editable: false,
      valueGetter: (params) => {
        if (params.node?.footer) {
          return '';
        }
        try {
          const budgetJson = JSON.parse(params.data?.budget_json || '{}');
          const percentages = budgetJson?.allocation?.percentages;

          if (percentages) {
            // Filter ad types based on allocation > 0%
            const activeAdTypes = [];
            if (percentages.sp_sd_percent > 0) activeAdTypes.push('SP,SD');
            if (percentages.sb_percent > 0) activeAdTypes.push('SB');
            if (percentages.st_percent > 0) activeAdTypes.push('ST');
            return activeAdTypes.join(',');
          }

          // Fallback to original ad_type_csv if no allocation data
          return params.data?.ad_type_csv || '';
        } catch (e) {
          return params.data?.ad_type_csv || '';
        }
      },
    },
    {
      field: 'allocation_sp_sd',
      headerName: 'SP+SD%',
      editable: false,
      valueGetter: (params) => {
        if (params.node?.footer) {
          return '';
        }
        try {
          const budgetJson = JSON.parse(params.data?.budget_json || '{}');
          const percentages = budgetJson?.allocation?.percentages;
          if (percentages && typeof percentages.sp_sd_percent !== 'undefined') {
            return percentages.sp_sd_percent;
          }
          // Default to 100% for SP+SD if no allocation data
          return 100;
        } catch (e) {
          return 100;
        }
      },
      valueFormatter: (params) => {
        if (params.node?.footer) {
          return '';
        }
        return params.value ? `${params.value}%` : '0%';
      },
    },
    {
      field: 'allocation_sb',
      headerName: 'SB%',
      editable: false,
      valueGetter: (params) => {
        if (params.node?.footer) {
          return '';
        }
        try {
          const budgetJson = JSON.parse(params.data?.budget_json || '{}');
          const percentages = budgetJson?.allocation?.percentages;
          return percentages?.sb_percent || 0;
        } catch (e) {
          return 0;
        }
      },
      valueFormatter: (params) => {
        if (params.node?.footer) {
          return '';
        }
        return params.value ? `${params.value}%` : '0%';
      },
    },
    {
      field: 'allocation_st',
      headerName: 'ST%',
      editable: false,
      valueGetter: (params) => {
        if (params.node?.footer) {
          return '';
        }
        try {
          const budgetJson = JSON.parse(params.data?.budget_json || '{}');
          const percentages = budgetJson?.allocation?.percentages;
          return percentages?.st_percent || 0;
        } catch (e) {
          return 0;
        }
      },
      valueFormatter: (params) => {
        if (params.node?.footer) {
          return '';
        }
        return params.value ? `${params.value}%` : '0%';
      },
    },
    {
      field: 'budget_group_name',
      headerName: 'Budget Group',
      editable: false,
      valueFormatter: (params) => {
        if (params.node?.footer) {
          return 'TOTAL/AVG';
        }
        return params.value;
      },
    },
    {
      headerName: 'Budgets',
      children: [
        {
          field: 'start_date',
          headerName: 'Start',
          valueFormatter: (params) => {
            if (params.node?.footer) {
              return '';
            }
            return params.value;
          },
        },
        {
          field: 'end_date',
          headerName: 'End',
          valueFormatter: (params) => {
            if (params.node?.footer) {
              return '';
            }
            return params.value;
          },
        },
        {
          field: 'budget_driver',
          headerName: 'Driver',
          valueFormatter: (params) => {
            if (params.node?.footer) {
              return '';
            }
            if (params.value) {
              return params.value.charAt(0).toUpperCase() + params.value.slice(1);
            }
            return params.value;
          },
        },
        {
          field: 'calcDaily',
          headerName: 'Daily',
          aggFunc: 'sum',
          valueGetter: params => {
            let dailyBudget = 0;
            if (params.data?.budget_driver === 'daily') {
              const budgetJson = JSON.parse(params.data.budget_json);
              // Try allocation.original.daily.budget first, fallback to daily.budget for backwards compatibility
              if (budgetJson.allocation?.original?.daily?.budget !== undefined) {
                dailyBudget = parseFloat(budgetJson.allocation.original.daily.budget);
              } else if (budgetJson.daily?.budget !== undefined) {
                dailyBudget = parseFloat(budgetJson.daily.budget);
              }
            } else if (params.data?.budget_driver === 'monthly') {
              const activeDaysInMonth = countActiveDaysInMonth(params.data.active_days_csv, params.data.start_date, params.data.end_date);
              if (activeDaysInMonth > 0) {
                const budgetJson = JSON.parse(params.data.budget_json);
                const monthKey = moment().format('M').toString();
                const monthName = budgetJson.monthly?.[monthKey]?.name?.toLowerCase();
                // Try allocation.original.monthly[monthName] first, fallback to monthly[monthKey].budget for backwards compatibility
                let monthBudget = 0;
                if (monthName && budgetJson.allocation?.original?.monthly?.[monthName] !== undefined) {
                  monthBudget = parseFloat(budgetJson.allocation.original.monthly[monthName]);
                } else if (budgetJson.monthly?.[monthKey]?.budget !== undefined) {
                  monthBudget = parseFloat(budgetJson.monthly[monthKey].budget);
                }
                dailyBudget = monthBudget / activeDaysInMonth;
              }
            }
            return dailyBudget;
          },
          valueFormatter: (params) => currencyFormatter(params),
        },
        {
          field: 'calcMonthly',
          headerName: 'Monthly',
          aggFunc: 'sum',
          valueGetter: (params) => {
            let currentMonthBudget = 0;
            if (params.data?.budget_driver === 'daily') {
              const activeDaysInMonth = countActiveDaysInMonth(params.data.active_days_csv, params.data.start_date, params.data.end_date);
              const budgetJson = JSON.parse(params.data.budget_json);
              // Try allocation.original.daily.budget first, fallback to daily.budget for backwards compatibility
              let dailyBudget = 0;
              if (budgetJson.allocation?.original?.daily?.budget !== undefined) {
                dailyBudget = parseFloat(budgetJson.allocation.original.daily.budget);
              } else if (budgetJson.daily?.budget !== undefined) {
                dailyBudget = parseFloat(budgetJson.daily.budget);
              }
              currentMonthBudget = dailyBudget * activeDaysInMonth;
            } else if (params.data?.budget_driver === 'monthly') {
              const budgetJson = JSON.parse(params.data.budget_json);
              const monthKey = moment().format('M').toString();
              const monthName = budgetJson.monthly?.[monthKey]?.name?.toLowerCase();
              // Try allocation.original.monthly[monthName] first, fallback to monthly[monthKey].budget for backwards compatibility
              if (monthName && budgetJson.allocation?.original?.monthly?.[monthName] !== undefined) {
                currentMonthBudget = parseFloat(budgetJson.allocation.original.monthly[monthName]);
              } else if (budgetJson.monthly?.[monthKey]?.budget !== undefined) {
                currentMonthBudget = parseFloat(budgetJson.monthly[monthKey].budget);
              }
            }
            return currentMonthBudget;
          },
          valueFormatter: (params) => currencyFormatter(params),
        },
        {
          field: 'calcActiveDays',
          headerName: 'Active Days',
          hide: !hideNonEssentialBudget,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            if (params.data?.active_days_csv) {
              return readableActiveDays(params.data.active_days_csv);
            }
            return 0;
          },
        },
        {
          field: 'calcActiveDaysInWeek',
          headerName: 'Active Days this Week',
          hide: !hideNonEssentialBudget,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            if (params.data?.active_days_csv) {
              return params.data.active_days_csv.match(/1/g).length;
            }
            return 0;
          },
        },
        {
          field: 'calcActiveDaysInMonth',
          headerName: 'Active Days this Month',
          hide: !hideNonEssentialBudget,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            if (params.data?.active_days_csv) {
              return countActiveDaysInMonth(params.data.active_days_csv, params.data.start_date, params.data.end_date);
            }
            return 0;
          },
        },
        {
          field: 'calcDaysInMonth',
          headerName: 'Days this Month',
          hide: !hideNonEssentialBudget,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            return moment().daysInMonth()
          },
        },
        {
          field: 'active_events',
          headerName: 'Active Events',
          hide: !hideNonEssentialBudget,

        },
        {
          field: 'upcoming_events',
          headerName: 'Upcoming Events',
          hide: !hideNonEssentialBudget,
        },
        {
          field: 'past_events',
          headerName: 'Past Events',
          hide: !hideNonEssentialBudget,
        },
        {
          field: 'reallocation',
          headerName: 'Reallocation',
          hide: true,
          suppressColumnsToolPanel: !adminView,
          aggFunc: 'sum',
          valueFormatter: params => currencyFormatter(params),
          valueGetter: (params) => parseFloat(JSON.parse(params.data.budget_json).daily?.reallocation || 0),
        },
      ],
    },
    {
      headerName: 'Cost Targets',
      children: [
        {
          field: 'cost_target_type',
          headerName: 'Cost Target Type',
          valueFormatter: (params) => {
            if (params.value === 'bnb') {
              return 'BNB';
            }
            if (params.value === 'blended') {
              return 'Blended';
            }
            return params.value;
          },
        },
        {
          field: 'cost_target_brand',
          headerName: 'Brand Cost Target',
          valueFormatter: (params) => {
            if (params.node?.footer) {
              return '';
            }
            if (params.data?.cost_target_metric === 'pacos') {
              return params.value.toString().concat('% PACoS');
            }
            if (params.data?.cost_target_metric === 'roas') {
              return params.value.toString().concat(' ROAS');
            }
            return params.value;
          },
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            return (params.data?.cost_target_brand || 0);
          },
        },
        {
          field: 'cost_target_nonbrand',
          headerName: 'Non-Brand Cost Target',
          valueFormatter: (params) => {
            if (params.node?.footer) {
              return '';
            }
            if (params.data?.cost_target_metric === 'pacos') {
              return params.value.toString().concat('% PACoS');
            }
            if (params.data?.cost_target_metric === 'roas') {
              return params.value.toString().concat(' ROAS');
            }
            return params.value;
          },
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            return (params.data?.cost_target_nonbrand || 0);
          },
        },
      ],
    },
    {
      headerName: 'Spend Controls',
      children: [
        {
          field: 'portfolio_ids_csv',
          headerName: 'Portfolio ID',
          hide: !hideNonEssentialSpendControls,
          suppressColumnsToolPanel: !adminView,
          valueGetter: params => (params.data?.portfolio_ids_csv || '').replace(/[^\-0-9.,]/g, ''),
        },
        {
          field: 'limitless_budget',
          headerName: 'Budget Type',
          hide: !adminView,
          suppressColumnsToolPanel: !adminView,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            const dispalyObj = lookupValue(limitlessBudgetOptions, params.data?.limitless_budget || 0);
            return dispalyObj?.label || '';
          },
        },
        {
          field: 'spend_priority',
          headerName: 'Spend Priority',
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            const dispalyObj = lookupValue(spendPriorityOptions, params.data?.spend_priority || '0');
            return dispalyObj?.label || '';
          },
        },
        {
          field: 'spend_tactic',
          headerName: 'Spend Tactic',
          hide: !hideNonEssentialSpendControls,
          suppressColumnsToolPanel: !adminView,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            const dispalyObj = lookupValue(spendTacticOptions, params.data?.spend_tactic || 'max_perf');
            return dispalyObj?.label || '';
          },
        },
        {
          field: 'spend_speed',
          headerName: 'Spend Speed',
          hide: !hideNonEssentialSpendControls,
          suppressColumnsToolPanel: !adminView,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            const dispalyObj = lookupValue(spendSpeedOptions, params.data?.spend_speed || 'speed_level_1');
            return dispalyObj?.label || '';
          },
        },
        {
          field: 'item_type',
          headerName: 'Item Type',
          hide: !hideNonEssentialSpendControls,
          suppressColumnsToolPanel: !adminView,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            const dispalyObj = lookupValue(itemTypeOptions, params.data?.item_type || '');
            return dispalyObj?.label || '';
          },
        },
        {
          field: 'item_selection',
          headerName: 'Item Selection',
          hide: !hideNonEssentialSpendControls,
          suppressColumnsToolPanel: !adminView,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            const dispalyObj = lookupValue(itemSelectionOptions, params.data?.item_selection || 'ALL');
            return dispalyObj?.label || '';
          },
        },
        {
          field: 'destination',
          headerName: 'Destination',
          hide: !hideNonEssentialSpendControls,
          suppressColumnsToolPanel: !adminView,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            const dispalyObj = lookupValue(destinationOptions, params.data?.destination || 'ON');
            return dispalyObj?.label || '';
          },
        },
      ],
    },
    {
      headerName: 'Metrics',
      children: [
        {
          field: 'includes',
          headerName: 'Data Filter',
          hide: !hideNonEssentialMetrics,
          valueGetter: params => {
            if (params.node?.footer) {
              return '';
            }
            const dispalyObj = lookupValue(includesOptions, params.data?.includes === '' ? '' : 'limitless');
            return dispalyObj?.label || '';
          },
        },
        {
          field: 'mtd_ppc_spend',
          headerName: 'MTD PPC Spend',
          hide: !hideNonEssentialMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
          valueGetter: p => {
            const isInternal = p.data?.includes !== '';
            return (isInternal ? p.data?.internal_spend : p.data?.all_spend) || 0;
          },
        },
        {
          field: 'mtd_ppc_sales',
          headerName: 'MTD PPC Sales',
          hide: !hideNonEssentialMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
          valueGetter: p => {
            const isInternal = p.data?.includes !== '';
            return (isInternal ? p.data?.internal_sales : p.data?.all_sales) || 0;
          },
        },
        {
          field: 'mtd_ppc_acos',
          headerName: 'MTD PPC ACoS',
          hide: !hideNonEssentialMetrics,
          editable: false,
          // aggregate from the column sums (spend ÷ sales)
          aggFunc: params => {
            const spend = params?.rowNode?.aggData?.mtd_ppc_spend ?? 0;
            const sales = params?.rowNode?.aggData?.mtd_ppc_sales ?? 0;
            return sales ? spend / sales : 0;
          },
          valueFormatter: formatDecimalToFixedTwoPercent,
          valueGetter: p => {
            const isInternal = p.data?.includes !== '';
            const spend = (isInternal ? p.data?.internal_spend : p.data?.all_spend) || 0;
            const sales = (isInternal ? p.data?.internal_sales : p.data?.all_sales) || 0;
            return sales ? spend / sales : 0;
          },
          headerTooltip: 'MTD PPC ACoS',
        },
        {
          field: 'mtd_ppc_roas',
          headerName: 'MTD PPC ROAS',
          hide: !hideNonEssentialMetrics,
          editable: false,
          aggFunc: params => {
            const spend = params?.rowNode?.aggData?.mtd_ppc_spend ?? 0;
            const sales = params?.rowNode?.aggData?.mtd_ppc_sales ?? 0;
            return spend ? sales / spend : 0;
          },
          valueFormatter: formatNumberOnePlaceDefaultToZero,
          valueGetter: p => {
            const isInternal = p.data?.includes !== '';
            const spend = (isInternal ? p.data?.internal_spend : p.data?.all_spend) || 0;
            const sales = (isInternal ? p.data?.internal_sales : p.data?.all_sales) || 0;
            return spend ? sales / spend : 0;
          },
          headerTooltip: 'MTD PPC ROAS',
        },
        // {
        //   field: 'mtd_orders',
        //   headerName: 'MTD Orders',
        //   hide: true,
        //   editable: false,
        //   aggFunc: 'sum',
        //   valueFormatter: formatNumberDefaultToZero,
        //   valueGetter: function calcValueGetter(params) {
        //     const isInternal = params.data?.includes !== '';
        //     const order = isInternal ? params.data?.internal_orders : params.data?.all_orders;
        //     return order;
        //   },
        // },
        {
          field: '',
          headerName: 'MTD Consumption Rate',
          hide: !hideNonEssentialMetrics,
          editable: false,
          valueFormatter: (params) => {
            if (params.node?.footer) {
              return '';
            }
            return formatDecimalToFixedTwoPercent(params);
          },
          valueGetter: function calcValueGetter(params) {
            if (params.node?.footer) {
              return '';
            }
            let totalBudget = 0;
            if (params.data?.budget_driver === 'daily') {
              const activeDaysInMonth = countActiveDaysInMonth(params.data.active_days_csv, params.data.start_date, params.data.end_date);
              const budgetJson = JSON.parse(params.data.budget_json);
              // Try allocation.original.daily.budget first, fallback to daily.budget for backwards compatibility
              let dailyBudget = 0;
              if (budgetJson.allocation?.original?.daily?.budget !== undefined) {
                dailyBudget = parseFloat(budgetJson.allocation.original.daily.budget);
              } else if (budgetJson.daily?.budget !== undefined) {
                dailyBudget = parseFloat(budgetJson.daily.budget);
              }
              totalBudget = dailyBudget * activeDaysInMonth;
            } else if (params.data?.budget_driver === 'monthly') {
              const budgetJson = JSON.parse(params.data.budget_json);
              const monthKey = moment().format('M').toString();
              const monthName = budgetJson.monthly?.[monthKey]?.name?.toLowerCase();
              // Try allocation.original.monthly[monthName] first, fallback to monthly[monthKey].budget for backwards compatibility
              if (monthName && budgetJson.allocation?.original?.monthly?.[monthName] !== undefined) {
                totalBudget = parseFloat(budgetJson.allocation.original.monthly[monthName]);
              } else if (budgetJson.monthly?.[monthKey]?.budget !== undefined) {
                totalBudget = parseFloat(budgetJson.monthly[monthKey].budget);
              }
            }

            let calcBudget = totalBudget; // limitlessBudgetFlag === 0
            const daysInMonth = moment().daysInMonth();
            if (params.data.limitlessBudgetFlag === 1) {
              calcBudget = totalBudget * 10;
            }
            if (params.data.limitlessBudgetFlag === 2) {
              calcBudget = totalBudget * 1.25;
            }

            if (params && params.data && params.data.internal_spend && reportDays.internalReportDays !== 0 && calcBudget && calcBudget !== 0 && daysInMonth !== 0 && params.data.includes === 'limitless') {
              return (params.data.internal_spend / reportDays.internalReportDays) / (calcBudget / daysInMonth);
            }
            if (params && params.data && params.data.all_spend && reportDays.allReportDays !== 0 && calcBudget && calcBudget !== 0 && daysInMonth !== 0) {
              return (params.data.all_spend / reportDays.allReportDays) / (calcBudget / daysInMonth);
            }
            return 0;
          },
          headerTooltip: 'Allocation',
        },
        {
          field: 'itemOnCount',
          headerName: 'Items ON in BG',
          hide: !hideNonEssentialMetrics,
          editable: false,
          aggFunc: 'sum',
        },
        // {
        //   field: 'calcBgDailyDollarPerItem',
        //   headerName: 'BG Daily $/Item',
        //   hide: !hideNonEssentialMetrics,
        //   editable: false,
        //   valueFormatter: (params) => {
        //     if (params.node?.footer) {
        //       return '';
        //     }
        //     return currencyFormatter(params);
        //   },
        //   valueGetter: function calcValueGetter(params) {
        //     const daysInMonth = moment().daysInMonth();
        //     if (params && params.data && params.data.itemOnCount && params.data.itemOnCount !== 0 && daysInMonth !== 0) {
        //       const totalBudget = (params.data.current || 0) + (params.data.reallocation || 0) + (params.data.carryover || 0) + (params.data.supplemental || 0);
        //       return (totalBudget / daysInMonth) / params.data.itemOnCount;
        //     }
        //     return 0;
        //   },
        // },
        {
          field: 'calcMtdDailyDollarPerItem',
          headerName: 'MTD Daily $/Item',
          hide: !hideNonEssentialMetrics,
          editable: false,
          valueFormatter: (params) => {
            if (params.node?.footer) {
              return '';
            }
            return currencyFormatter(params);
          },
          valueGetter: function calcValueGetter(params) {
            const isInternal = params.data?.includes !== '';
            const spend = isInternal ? params.data?.internal_spend : params.data?.all_spend;
            if (params && params.data && spend && params.data.itemOnCount && params.data.itemOnCount !== 0 && reportDays.internalReportDays !== 0) {
              return (spend / reportDays.internalReportDays) / params.data.itemOnCount;
            }
            return 0;
          },
        },
      ],
    },
    {
      headerName: 'Advanced Metrics',
      suppressColumnsToolPanel: !adminView,
      children: [
        {
          field: 'internal_advertised_sku_sales',
          headerName: 'MTD Limitless Advertised Sku Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'internal_brand_sales',
          headerName: 'MTD Limitless Brand Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'internal_direct_sales',
          headerName: 'MTD Limitless Direct Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'internal_related_sales',
          headerName: 'MTD Limitless Related Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'internal_ntb_orders',
          headerName: 'MTD Limitless NTB Orders',
          hide: !adminView,
          editable: false,
          aggFunc: 'sum',
          valueFormatter: formatNumberDefaultToZero,
        },
        {
          field: 'internal_ntb_revenue',
          headerName: 'MTD Limitless NTB Revenue',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'internal_other_sku_sales',
          headerName: 'MTD Limitless Other SKU Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'internal_in_store_orders',
          headerName: 'MTD Limitless In Store Orders',
          hide: !adminView,
          editable: false,
          aggFunc: 'sum',
          valueFormatter: formatNumberDefaultToZero,
        },
        {
          field: 'internal_in_store_sales',
          headerName: 'MTD Limitless In Store Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'all_advertised_sku_sales',
          headerName: 'MTD ALL Advertised Sku Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'all_brand_sales',
          headerName: 'MTD ALL Brand Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'all_direct_sales',
          headerName: 'MTD ALL Direct Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'all_related_sales',
          headerName: 'MTD ALL Related Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'all_ntb_orders',
          headerName: 'MTD ALL NTB Orders',
          hide: true,
          editable: false,
          aggFunc: 'sum',
          valueFormatter: formatNumberDefaultToZero,
        },
        {
          field: 'all_ntb_revenue',
          headerName: 'MTD ALL NTB Revenue',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'all_other_sku_sales',
          headerName: 'MTD ALL Other SKU Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
        {
          field: 'all_in_store_orders',
          headerName: 'MTD ALL In Store Orders',
          hide: true,
          editable: false,
          aggFunc: 'sum',
          valueFormatter: formatNumberDefaultToZero,
        },
        {
          field: 'all_in_store_sales',
          headerName: 'MTD ALL In Store Sales',
          hide: !hideNonEssentialAdvancedMetrics,
          aggFunc: 'sum',
          editable: false,
          valueFormatter: currencyFormatter,
        },
      ],
    },
    {
      headerName: 'Sparklines',
      children: [
        {
          field: 'spendChange',
          headerName: 'Spend Change',
          hide: !hideNonEssentialSparklines,
          cellRenderer: 'agSparklineCellRenderer',
          cellRendererParams: params => {
            const isInternal = params.data?.includes !== '';
            const sparklineData = isInternal ? params.data?.internalSpendChange : params.data?.allSpendChange;

            return {
              sparklineOptions: {
                type: 'area',
                data: sparklineData,
                fill: 'rgba(70, 133, 244, 0.4)',
                stroke: 'rgb(0, 0, 255)',
                marker: {
                  enabled: true,
                  size: 0,
                  itemStyler: (itemParams) => {
                    if (itemParams.highlighted) {
                      return { size: 7 };
                    }
                    return { size: 7 };
                  },
                },
                highlightStyle: {
                  item: {
                    fill: 'rgb(143,185,77)',
                    strokeWidth: 0,
                  },
                },
                axis: {
                  type: 'category',
                  stroke: 'rgb(204, 204, 235)',
                },
              },
            };
          },
        },
        {
          field: 'salesChange',
          headerName: 'Sales Change',
          hide: !hideNonEssentialSparklines,
          cellRenderer: 'agSparklineCellRenderer',
          cellRendererParams: params => {
            const isInternal = params.data?.includes !== '';
            const sparklineData = isInternal ? params.data?.internalSalesChange : params.data?.allSalesChange;
            return {
              sparklineOptions: {
                type: 'area',
                data: sparklineData,
                fill: 'rgba(70, 133, 244, 0.4)',
                stroke: 'rgb(0, 0, 255)',
                marker: {
                  enabled: true,
                  size: 0,
                  itemStyler: (itemParams) => {
                    if (itemParams.highlighted) {
                      return { size: 7 };
                    }
                    return { size: 7 };
                  },
                },
                highlightStyle: {
                  item: {
                    fill: 'rgb(143,185,77)',
                    strokeWidth: 0,
                  },
                },
                axis: {
                  type: 'category',
                  stroke: 'rgb(204, 204, 235)',
                },
              },
            };
          },
        },
        {
          field: 'acosChange',
          headerName: 'ACoS Change',
          hide: !hideNonEssentialSparklines,
          cellRenderer: 'agSparklineCellRenderer',
          cellRendererParams: params => {
            const isInternal = params.data?.includes !== '';
            const sparklineData = isInternal ? params.data?.internalAcosChange : params.data?.allAcosChange;
            return {
              sparklineOptions: {
                type: 'area',
                data: sparklineData,
                fill: 'rgba(70, 133, 244, 0.4)',
                stroke: 'rgb(0, 0, 255)',
                marker: {
                  enabled: true,
                  size: 0,
                  itemStyler: (itemParams) => {
                    if (itemParams.highlighted) {
                      return { size: 7 };
                    }
                    return { size: 7 };
                  },
                },
                highlightStyle: {
                  item: {
                    fill: 'rgb(143,185,77)',
                    strokeWidth: 0,
                  },
                },
                axis: {
                  type: 'category',
                  stroke: 'rgb(204, 204, 235)',
                },
              },
            };
          },
        },
      ],
    },
  ];

  const onBtnExport = () => {
    const exportPrefix = getExportPrefix(`${retailerConfig.prefix}-daily-budget-groups`, sellerSelect, true);
    const fileName = `${exportPrefix}.csv`;
    gridRef.current.api.exportDataAsCsv({ fileName });
  };

  const defaultColDef = {
    editable: false,
    sortable: true,
    filter: true,
    resizable: true,
  }

  return (
    <>
      <div style={{ height: '100%', width: '100%' }}>
        <GroupEditModal
          showEditModal={showEditModal}
          currentEditRow={editRowData}
          changeShowEditModal={changeShowEditModal}
          setForceRefresh={setForceRefresh}
          budgetAccountData={budgetAccountData}
          activeDayOptions={activeDayOptions}
          disableButton={disableButton}
          setDisableButton={setDisableButton}
          setShowEditModal={setShowEditModal}
          retailerConfig={retailerConfig}
        />
        <Panel
          lg={12}
          md={12}
          title={`${title} Group Settings`}
          subhead="Used to manage budgets at a 'budget group' level"
          parentRefresh={panelLoad || allGraphLoad || internalGraphLoad}
          getData={() => getData()}
        >
          <div
            className={gridTheme}
            style={{ width: '100%' }}
          >
            <br />
            <div className="topbar__wrapper">
              <div>
                Show Additional Columns:
                <ToggleWrapOuter>
                  <ToggleSlide
                    name="Budgets"
                    setFunction={() => setHideNonEssentialBudget(!hideNonEssentialBudget)}
                    currentState={hideNonEssentialBudget}
                    bgColor={platfromSpec.colorPrimary}
                  />
                  {user && user.access === 'admin' && adminView && (
                    <ToggleSlide
                      name="Spend Controls"
                      setFunction={() => setHideNonEssentialSpendControls(!hideNonEssentialSpendControls)}
                      currentState={hideNonEssentialSpendControls}
                      bgColor={platfromSpec.colorPrimary}
                    />
                  )}
                  <ToggleSlide
                    name="Metrics"
                    setFunction={() => setHideNonEssentialMetrics(!hideNonEssentialMetrics)}
                    currentState={hideNonEssentialMetrics}
                    bgColor={platfromSpec.colorPrimary}
                  />
                  {user && user.access === 'admin' && adminView && (
                    <ToggleSlide
                      name="Advanced Metrics"
                      setFunction={() => setHideNonEssentialAdvancedMetrics(!hideNonEssentialAdvancedMetrics)}
                      currentState={hideNonEssentialAdvancedMetrics}
                      bgColor={platfromSpec.colorPrimary}
                    />
                  )}
                  <ToggleSlide
                    name="Sparklines"
                    setFunction={() => setHideNonEssentialSparklines(!hideNonEssentialSparklines)}
                    currentState={hideNonEssentialSparklines}
                    bgColor={platfromSpec.colorPrimary}
                  />
                  {user && user.access === 'admin' && (
                    <ToggleSlide
                      name="AdminView"
                      setFunction={() => setAdminView(!adminView)}
                      currentState={adminView}
                      bgColor={platfromSpec.colorPrimary}
                    />
                  )}
                </ToggleWrapOuter>
              </div>
              <div className="topbar__right">
                <div className="form__button-toolbar">
                  <button className="btn btn-primary" onClick={() => changeShowEditModal(addNewDefault)} type="button">Add Budget Group</button>
                  <button className="btn btn-primary" onClick={onBtnExport} type="button">Export</button>
                </div>
              </div>
            </div>
            <br />
            <div className={gridTheme} style={{ height: '50vh', width: '100%' }}>
              <AgGridReact
                ref={gridRef}
                columnDefs={columnDefs}
                rowData={combinedItems}
                defaultColDef={defaultColDef}
                groupIncludeTotalFooter
                aggFuncs={aggFuncs}
                onGridReady={onGridReady}
                suppressScrollOnNewData
                enableRangeSelection
                rowSelection={'multiple'}
              />
            </div>
          </div>
        </Panel>
      </div>
    </>
  );
}

BudgetTable.propTypes = {
  gridTheme: PropTypes.string,
  internalGraphLoad: PropTypes.bool.isRequired,
  allGraphLoad: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  retailer: PropTypes.string.isRequired,
  internalById: PropTypes.shape().isRequired,
  allById: PropTypes.shape().isRequired,
};

BudgetTable.defaultProps = {
  gridTheme: 'ag-theme-balham',
};
